import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { fr } from 'date-fns/locale';

import { Arrow, Time } from 'icons/untitledui';
import dayjs from 'shared/helpers/dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import useClickOutside from 'shared/hooks/useClickOutside';
import { calendarPopperSx } from 'dashboard/styles/Filters.style';
import * as Styled from 'form/styles/FormEditView.style';

import { DATE_DISPLAY_FORMAT, DATE_PARSE_FORMATS } from './constants';

dayjs.extend(customParseFormat);

interface InlineDateFieldProps {
  value: string;
  onChange: (next: string) => void;
  placeholderText: string;
}

export default function InlineDateField({
  value,
  onChange,
  placeholderText,
}: InlineDateFieldProps): JSX.Element {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'fr' ? fr : undefined;

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);

  useClickOutside([anchorRef, popperRef], () => setOpen(false), open);

  const selectedDate = useMemo(() => {
    if (!value) return null;
    const strict = dayjs(value, DATE_PARSE_FORMATS, true);
    const parsed = strict.isValid() ? strict : dayjs(value);
    return parsed.isValid() ? parsed.toDate() : null;
  }, [value]);

  const displayValue = value || placeholderText;

  return (
    <>
      <Styled.FieldTrigger
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={placeholderText}
        $hasValue={!!value}
      >
        <span>{displayValue}</span>
        <Time.Calendar />
      </Styled.FieldTrigger>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        <DesktopDatePicker
          value={selectedDate}
          open={open}
          onClose={() => setOpen(false)}
          onChange={(date: Date | null) => {
            if (date && dayjs(date).isValid()) {
              onChange(dayjs(date).format(DATE_DISPLAY_FORMAT));
            } else {
              onChange('');
            }
            setOpen(false);
          }}
          slots={{
            leftArrowIcon: Arrow.ChevronLeft,
            rightArrowIcon: Arrow.ChevronRight,
          }}
          slotProps={{
            textField: {
              sx: Styled.HiddenDateTextFieldSx,
            },
            popper: {
              ref: popperRef,
              anchorEl: anchorRef.current,
              placement: 'bottom-start',
              sx: calendarPopperSx,
            },
          }}
        />
      </LocalizationProvider>
    </>
  );
}
